import { produce } from "immer";
import { Store } from "reducers/rootReducer";
import { of, from, Observable } from "rxjs";
import { AxiosRequestConfig, AxiosError } from "axios";
import { endpoints } from "config";
import { loadTokenResource, saveTokenResource } from "helpers/auth";
import api from "api";
import {
  map,
  tap,
  switchMap,
  catchError,
  ignoreElements,
} from "rxjs/operators";
import { combineEpics, ofType, ActionsObservable } from "redux-observable";
import { createSelector } from "reselect";

export type SignInAttempt = {
  emailAddress: string;
  password: string;
  rememberMe: boolean;
};

type SignInError = {
  error: null | string;
  error_description: string;
  error_uri: null;
};

type SignInOkay = {
  status: "Success";
  accessToken: string;
  tokentype: string;
  expiresIn: number;
  refreshToken: string;
};

type GoogleAuth = {
  [key: string]: any
}

type CheckIdpAttempt = { emailAddress: string; };
type CheckIdpOkay = { status: 'Success'; type: string; redirect: string; };
type CheckIdpError = { status: 'Error'; error_description: string; /* other fields */ };
type CheckIdpResponse = CheckIdpOkay | CheckIdpError;


type SignInResponse = SignInOkay | SignInError;


const isResponseOkay = (r: SignInResponse): r is SignInOkay =>
  (r as SignInOkay).status === "Success";

const isIdpResponseOkay = (r: CheckIdpResponse): r is CheckIdpOkay => r.status === 'Success';

/* ACTION CREATORS */
export const signIn = (payload: SignInAttempt): Action => ({
    type: SIGN_IN,
    payload,
});

export const signOut = (payload?: string): Action => ({
    type: SIGN_OUT,
    payload,
});

export const checkSignedIn = (): Action => ({
    type: CHECK_SIGNED_IN,
});

export const googleSignIn = (payload: GoogleAuth): Action => ({
    type: SIGN_IN_GOOGLE,
    payload
})

export const ssoSignIn = (): Action => ({
    type: SIGN_IN_AD
})

const signInOkay = (payload: SignInOkay): Action => ({
    type: SIGN_IN_OKAY,
    payload,
});

const signInError = (payload: SignInError): Action => ({
    type: SIGN_IN_ERROR,
    payload,
});


export const checkIdp = (payload: { emailAddress: string }): Action => ({
    type: CHECK_IDP,
    payload,
});

export const checkIdpSuccess = (payload: CheckIdpOkay): Action => ({
    type: CHECK_IDP_SUCCESS,
    payload,
});

export const checkIdpError = (payload: CheckIdpError):Action => ({
    type: CHECK_IDP_ERROR,
    payload,
});

const showApp = (payload: boolean): Action => ({
    type: SHOW_APP,
    payload,
});


/* STATE */
type AuthStatus = "signed_out" | "signed_in" | "pending" | "checked_idp" | "uses_internal_auth" | "uses_idp";


type State = {
  authStatus: AuthStatus;
  appVisible: boolean;
  message: string;
};

const initialState: State = {
  authStatus: "signed_out",
  appVisible: false,
  message: "",
};

/* ACTIONS */
export const SIGN_OUT = "app/auth/SIGN_OUT";
const SIGN_IN = "app/auth/SIGN_IN";
const SIGN_IN_OKAY = "app/auth/SIGN_IN_OKAY";
const SIGN_IN_ERROR = "app/auth/SIGN_IN_ERROR";
const SHOW_APP = "app/auth/SHOW_APP";
const CHECK_SIGNED_IN = "app/auth/CHECK_SIGNED_IN";
const SIGN_IN_GOOGLE = "app/auth/SIGN_IN_GOOGLE";
const SIGN_IN_AD = "app/auth/SIGN_IN_AD";
const CHECK_IDP = "app/auth/CHECK_IDP";
const CHECK_IDP_SUCCESS  = "app/auth/CHECK_IDP_SUCCESS ";
const CHECK_IDP_ERROR = "app/auth/CHECK_IDP_ERROR";

type Action =
  | { type: typeof SIGN_IN; payload: SignInAttempt }
  | { type: typeof SIGN_OUT; payload?: string }
  | { type: typeof CHECK_SIGNED_IN }
  | { type: typeof SHOW_APP; payload: boolean }
  | { type: typeof SIGN_IN_OKAY; payload: SignInOkay }
  | { type: typeof SIGN_IN_ERROR; payload: SignInError }
  | { type: typeof SIGN_IN_GOOGLE; payload: GoogleAuth }
  | { type: typeof CHECK_IDP; payload: CheckIdpAttempt }
  | { type: typeof CHECK_IDP_SUCCESS; payload: CheckIdpOkay }
  | { type: typeof CHECK_IDP_ERROR; payload: CheckIdpError }
  | { type: typeof SIGN_IN_AD}

/* REDUCER */
export default function reducer(state = initialState, action: Action): State {
  return produce(state, (draft) => {
    switch (action.type) {
      case SHOW_APP: // DO I NEED THIS ACTION, WHAT IS ITS USE?
        draft.appVisible = true;
        // Is the user already signed in?
        if (action.payload) draft.authStatus = "signed_in";
        break;      case SIGN_OUT:
        draft.authStatus = "signed_out";
        break;
      case SIGN_IN:
        draft.authStatus = "pending";
        draft.message = "";
        break;
      case SIGN_IN_OKAY:
        draft.authStatus = "signed_in";
        break;
      case SIGN_IN_ERROR:
          //debugger
        draft.authStatus = "signed_out";
        draft.message = action.payload.error_description;
        break;
      case SIGN_IN_GOOGLE:
        //console.log("Got to case");
        draft.authStatus = "pending";
        //draft.message = "Google";
        break;
    case CHECK_IDP_SUCCESS:
        //console.log("Got to case");
        if (action.payload.type === 'IDP') {
            draft.authStatus = "uses_idp";
            draft.message = action.payload.redirect;
        }
        else {
            draft.authStatus = "uses_internal_auth";
            draft.message = "";
        }
        break;
    }
  });
}

/* EPICS */
function makeRequest<T>(config: AxiosRequestConfig) {
  return from(api.request<T>(config));
}

function saveResponse(r: SignInResponse) {
  if (isResponseOkay(r)) {
    saveTokenResource({ ...r, refreshTokenExpires: new Date("2099-01-01") });
  }
}
function saveIdpResponse(r: CheckIdpResponse) {

}


function checkResponse(r: SignInResponse) {
  if (isResponseOkay(r)) return signInOkay(r);
  return signInError(r);
}

function checkIdpResponse(r: CheckIdpResponse) {
    if (isIdpResponseOkay(r)) return checkIdpSuccess(r);
    return checkIdpError(r);
}



/** Attempt to sign in and handle response, cancel if we sign out while a request pending. */
const signInEpic = (action$: ActionsObservable<any>): Observable<Action> =>
  action$.pipe(
    ofType(SIGN_IN),
    switchMap((attempt) =>
      makeRequest<SignInResponse>({
        url: endpoints.auth.login,
        data: attempt.payload,
        method: "post",
      }).pipe(
        tap((res) => saveResponse(res.data)),
        map((res) => checkResponse(res.data)),
        catchError((err: AxiosError) => {
          return of(
            signInError({
              error: null,
              error_description: err.response?.data.error_description,
              error_uri: null,
            })
          );
        })
        //takeUntil(action$.pipe(ofType(SIGN_OUT, SIGN_IN_ERROR)))
      )
    )
  );

/* Clear the local storage on sign out, so we don't appear logged in on refresh. */
const signOutEpic = (action$: ActionsObservable<any>) =>
  action$.pipe(
    ofType(SIGN_OUT),
    tap(() => window && window.localStorage.clear()),
    ignoreElements()
  );

const checkSignedInEpic = (action$: ActionsObservable<any>) =>
  action$.pipe(
    ofType(CHECK_SIGNED_IN),
    map(loadTokenResource),
    map((r) => {
        return(r !== null);
    }),
    map(showApp)
  );

const checkGoogleAuthEpic = (action$: ActionsObservable<any>): Observable<Action> =>
    action$.pipe(
        //tap(console.log),
        ofType(SIGN_IN_GOOGLE),
        //tap(console.log),
        switchMap((attempt) =>
            makeRequest<SignInResponse>({
              url: endpoints.auth.googleLogin,
              data: attempt.payload,
              method: "post",
            }).pipe(
                tap(console.log),
                tap((res) => saveResponse(res.data)),
                map((res) => checkResponse(res.data)),
                catchError((err: AxiosError) => {
                    err.response && console.log(err.response.data.error_description)
                  return of(
                      signInError({
                        error: null,
                        error_description: err.response?.data.error_description,
                        error_uri: null,
                      })
                  );
                })
            )
        )
    )


const checkAdAuthEpic = (action$: ActionsObservable<any>): Observable<Action> =>
    action$.pipe(
        //tap(console.log),
        ofType(SIGN_IN_AD),
        tap(console.log),
        switchMap(() =>
            makeRequest<SignInResponse>({
                url: endpoints.auth.adLogin,
                data: {},
                method: "post",
                withCredentials: true
            }).pipe(
                tap(console.log),
                tap((res) => saveResponse(res.data)),
                map((res) => checkResponse(res.data)),
                catchError((err: AxiosError) => {
                    err.response && console.log(err.response.data.error_description)
                    return of(
                        signInError({
                            error: null,
                            error_description: err.response?.data.error_description,
                            error_uri: null,
                        })
                    );
                })
            )
        )
    )



const checkIdpEpic = (action$: ActionsObservable<any>): Observable<Action> =>
    action$.pipe(
        ofType(CHECK_IDP),
        tap(console.log),
        switchMap((attempt) =>
            makeRequest<CheckIdpResponse>({
                url: endpoints.auth.checkIdp,
                data: attempt.payload,
                method: "post",
                withCredentials: false
            }).pipe(
                tap((res) => saveIdpResponse(res.data)),
                map((res) => checkIdpResponse(res.data)),
                catchError((err: AxiosError) => {
                    err.response && console.log(err.response.data.error_description)
                    return of(
                        checkIdpError({
                            error_description: err.response?.data.error_description,
                            status: 'Error'
                        })
                    );
                })
            )
        )
    )

export const authEpic = combineEpics(
    signInEpic,
    signOutEpic,
    checkSignedInEpic,
    checkGoogleAuthEpic,
    checkAdAuthEpic,
    checkIdpEpic);


/* SELECTORS */
export const selectAuthStatus = (app: Store) => app.auth.authStatus;
export const selectSignedIn = createSelector(
  selectAuthStatus,
  (status) => status === "signed_in"
);
export const selectAuthMessage = (app: Store) => app.auth.message;
export const selectAppVisible = (app: Store) => app.auth.appVisible;
